import { useCallback, useEffect, useRef, useState } from 'react';
import { Dialog, IconButton, Tooltip } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DailyIframe, { DailyCall, DailyEvent } from '@daily-co/daily-js';
import { DailyAudio, DailyProvider } from '@daily-co/daily-react';

import HairCheck from '../HairCheck/HairCheck';
import CallScreen from '../CallScreen/CallScreen';
import api, { copyConversationHistory } from '../../lib/api/Call';
import { Persona } from '../../lib/api/Persona';
import { UserInfo, useUserInfoContext } from '../../lib/contexts/UserInfoContext';
import { useChatSessionsContext } from '../../lib/contexts/ChatSessionsContext';
import './VideoCallModal.css';

type Phase = 'haircheck' | 'creating' | 'joining' | 'joined' | 'error';

interface CallLifecycle {
  generation: number;
  callObject: DailyCall | null;
  phase: Phase;
  conversationId: string | null;
  originalChatId: string | null;
  wasJoined: boolean;
  userInfo: UserInfo | undefined;
  persona: Persona;
  teardownPromise?: Promise<void>;
}

interface VideoCallModalProps {
  open: boolean;
  onClose: () => void;
  persona: Persona | null;
  /** Fired after a successful call ends and any post-call sync has run. The argument is
   *  the chat session id that holds the (now-merged) transcript — text chat should load
   *  this id so the call utterances appear inline.
   *
   *  ponytail: this is read from a ref at teardown time (always the latest callback the
   *  parent passed), not frozen at lifecycle creation. That's correct for /text-chat,
   *  where the callback only closes over per-render state (e.g. userInfo) that must stay
   *  fresh. It is NOT safe on its own for a caller that writes into per-boundary/generation
   *  state keyed by `continuationBoundaryKey` (e.g. a learning-workspace lease): if the
   *  boundary rotates while a call from the old boundary is still tearing down, this will
   *  invoke the NEW callback with the OLD call's chat id, and generation checks that only
   *  compare against "the current generation" won't catch it, because the callback itself
   *  is already bound to the new generation. A caller in that position must have the
   *  callback carry (or close over) the boundary/generation it was issued for and reject
   *  the write itself if that no longer matches current state. */
  onCallEnded?: (chatId: string) => void;
  presentation?: 'dialog' | 'inline';
  chapterSlug?: string;
  /** Lesson-opening text seeded as the first transcript entry for inline (learning workspace) calls. */
  openingPreamble?: string;
  onTeachingInteraction?: () => void;
  onConversationIdentified?: (conversationId: string) => void;
  onTranscriptVisibilityChange?: (visible: boolean) => void;
  continuationBoundaryKey?: string;
  continuationChatId?: string;
}

const DEVICE_PREP_BUDGET_MS = 15000;
const CREATE_CONVERSATION_BUDGET_MS = 30000;
const JOIN_CONVERSATION_BUDGET_MS = 45000;
const HISTORY_SYNC_BUDGET_MS = 10000;

function withTimeout<T>(promise: Promise<T>, timeoutMs: number, message: string) {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error(message)), timeoutMs);
    promise
      .then((value) => { window.clearTimeout(timer); resolve(value); })
      .catch((err) => { window.clearTimeout(timer); reject(err); });
  });
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

export default function VideoCallModal({
  open,
  onClose,
  persona,
  onCallEnded,
  presentation = 'dialog',
  chapterSlug,
  openingPreamble,
  onTeachingInteraction,
  onConversationIdentified,
  onTranscriptVisibilityChange,
  continuationBoundaryKey,
  continuationChatId,
}: VideoCallModalProps) {
  const { userInfo } = useUserInfoContext();
  const { selectedChatSession, promoteChatSession } = useChatSessionsContext();
  const [callObject, setCallObject] = useState<DailyCall | null>(null);
  const [phase, setPhase] = useState<Phase>('haircheck');
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [roomUrl, setRoomUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const hasPersona = persona !== null;

  const mountedRef = useRef(true);
  const lifecycleGenerationRef = useRef(0);
  const requestAttemptRef = useRef(0);
  const activeLifecycleRef = useRef<CallLifecycle | null>(null);
  const pendingTeardownRef = useRef<Promise<void>>(Promise.resolve());
  const endingConversationPromisesRef = useRef(new Map<string, Promise<boolean>>());
  const endedConversationIdsRef = useRef(new Set<string>());
  const originalChatIdRef = useRef<string | null>(null);
  const onCallEndedRef = useRef(onCallEnded);
  const onConversationIdentifiedRef = useRef(onConversationIdentified);
  const presentationRef = useRef(presentation);
  const promoteChatSessionRef = useRef(promoteChatSession);
  const lifecycleInputsRef = useRef({ userInfo, persona, selectedChatSession, continuationChatId });
  onCallEndedRef.current = onCallEnded;
  onConversationIdentifiedRef.current = onConversationIdentified;
  presentationRef.current = presentation;
  promoteChatSessionRef.current = promoteChatSession;
  lifecycleInputsRef.current = { userInfo, persona, selectedChatSession, continuationChatId };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const resetLifecycleState = useCallback(() => {
    if (!mountedRef.current) return;
    setCallObject(null);
    setConversationId(null);
    setRoomUrl(null);
    setError(null);
    setPhase('haircheck');
  }, []);

  const endConversationOnce = useCallback((endingUserInfo: UserInfo | undefined, endingId: string) => {
    if (endedConversationIdsRef.current.has(endingId)) return Promise.resolve(true);
    const existing = endingConversationPromisesRef.current.get(endingId);
    if (existing) return existing;

    let request: Promise<boolean>;
    try {
      request = Promise.resolve(api.endConversation(endingUserInfo, endingId));
    } catch {
      request = Promise.resolve(false);
    }
    const tracked = request.catch(() => false);
    endingConversationPromisesRef.current.set(endingId, tracked);
    void tracked.then((ended) => {
      if (ended) endedConversationIdsRef.current.add(endingId);
    }).finally(() => {
      if (endingConversationPromisesRef.current.get(endingId) === tracked) {
        endingConversationPromisesRef.current.delete(endingId);
      }
    });
    return tracked;
  }, []);

  useEffect(() => {
    const handlePageHide = () => {
      const lifecycle = activeLifecycleRef.current;
      if (lifecycle?.conversationId) {
        endConversationOnce(lifecycle.userInfo, lifecycle.conversationId);
      }
    };
    window.addEventListener('pagehide', handlePageHide);
    return () => window.removeEventListener('pagehide', handlePageHide);
  }, [endConversationOnce]);

  const teardownLifecycle = useCallback((lifecycle: CallLifecycle) => {
    if (lifecycle.teardownPromise) return lifecycle.teardownPromise;

    lifecycle.teardownPromise = (async () => {
      const co = lifecycle.callObject;
      const cid = lifecycle.conversationId;
      const wasJoined = lifecycle.wasJoined;

      // Stop vendor billing as soon as teardown begins. Transcript reconciliation is
      // best-effort follow-up work and must not keep the Tavus room alive.
      if (cid) endConversationOnce(lifecycle.userInfo, cid);

      if (co) {
        try {
          if (['joining', 'joined'].includes(lifecycle.phase)) {
            try { await co.leave(); } catch { /* ignore */ }
          }
        } catch (e) {
          console.warn('[VideoCallModal] leave error', e);
        }
      }

      // Reverse-sync call utterances back into the original chat session, OR promote
      // the call's conversation_id if the user had no prior chat.
      let resolvedChatId: string | null = null;
      if (wasJoined && cid && lifecycle.userInfo) {
        const targetChatId = lifecycle.originalChatId;
        if (targetChatId && targetChatId !== cid) {
          try {
            const historyCopied = await withTimeout(
              copyConversationHistory(
                lifecycle.userInfo,
                cid,
                targetChatId,
                lifecycle.persona.id || '',
              ),
              HISTORY_SYNC_BUDGET_MS,
              'Syncing the call transcript took too long.',
            );
            resolvedChatId = historyCopied ? targetChatId : cid;
          } catch (e) {
            console.warn('[VideoCallModal] reverse history sync failed', e);
            resolvedChatId = cid;
          }
        } else if (!targetChatId) {
          try {
            promoteChatSessionRef.current(cid, lifecycle.persona.id);
          } catch (e) {
            console.warn('[VideoCallModal] failed to promote conversation to chat session', e);
          }
          resolvedChatId = cid;
        }
      }

      if (co) {
        try {
          await co.destroy();
        } catch (e) {
          console.warn('[VideoCallModal] destroy error', e);
        }
      }

      if (wasJoined && resolvedChatId) {
        onCallEndedRef.current?.(resolvedChatId);
      }
    })();

    return lifecycle.teardownPromise;
  }, [endConversationOnce]);

  // Own one lifecycle per open epoch. Disposing it handles both close and true unmount.
  useEffect(() => {
    if (!open || !hasPersona) return;

    const inputs = lifecycleInputsRef.current;
    if (!inputs.persona) return;
    const previousTeardown = pendingTeardownRef.current;
    const lifecycle: CallLifecycle = {
      generation: ++lifecycleGenerationRef.current,
      callObject: null,
      phase: 'haircheck',
      conversationId: null,
      originalChatId: inputs.continuationChatId
        ?? (presentationRef.current === 'inline' ? null : inputs.selectedChatSession ?? null),
      wasJoined: false,
      userInfo: inputs.userInfo,
      persona: inputs.persona,
    };
    activeLifecycleRef.current = lifecycle;
    originalChatIdRef.current = lifecycle.originalChatId;
    resetLifecycleState();

    (async () => {
      try {
        await previousTeardown;
        if (activeLifecycleRef.current !== lifecycle) return;
        const existing = DailyIframe.getCallInstance();
        if (existing) {
          try { await existing.destroy(); } catch { /* ignore */ }
        }
        if (activeLifecycleRef.current !== lifecycle) return;
        const co = DailyIframe.createCallObject();
        lifecycle.callObject = co;
        if (activeLifecycleRef.current !== lifecycle) {
          try { await co.destroy(); } catch { /* ignore */ }
          return;
        }
        setCallObject(co);
        setPhase('haircheck');
        await withTimeout(
          co.startCamera(),
          DEVICE_PREP_BUDGET_MS,
          'Camera setup took too long. Please check permissions and retry.',
        );
      } catch (e: any) {
        if (activeLifecycleRef.current === lifecycle) {
          lifecycle.phase = 'error';
          setError(e?.message || 'Could not prepare camera and microphone access.');
          setPhase('error');
        }
      }
    })();

    return () => {
      if (activeLifecycleRef.current === lifecycle) {
        activeLifecycleRef.current = null;
        lifecycleGenerationRef.current += 1;
        requestAttemptRef.current += 1;
        resetLifecycleState();
      }
      const teardown = teardownLifecycle(lifecycle);
      pendingTeardownRef.current = Promise.allSettled([
        pendingTeardownRef.current,
        teardown,
      ]).then(() => undefined);
    };
  }, [continuationBoundaryKey, open, hasPersona, resetLifecycleState, teardownLifecycle]);

  // Listen to Daily lifecycle events to update phase
  useEffect(() => {
    if (!callObject) return;
    const events: DailyEvent[] = ['joined-meeting', 'left-meeting', 'error', 'camera-error'];
    const handle = () => {
      const lifecycle = activeLifecycleRef.current;
      if (!lifecycle || lifecycle.callObject !== callObject) return;
      const state = callObject.meetingState();
      if (state === 'joined-meeting') {
        lifecycle.phase = 'joined';
        lifecycle.wasJoined = true;
        setPhase('joined');
      }
      else if (state === 'left-meeting') onClose();
      else if (state === 'error') {
        lifecycle.phase = 'error';
        setPhase('error');
        setError('The call disconnected before it could fully connect.');
      }
    };
    events.forEach((ev) => callObject.on(ev, handle));
    return () => {
      events.forEach((ev) => callObject.off(ev, handle));
    };
  }, [callObject, onClose]);

  const createCall = useCallback(
    async (
      selectedPersona: Persona,
      chatId?: string,
      conversationalContext?: Array<{ role: 'user' | 'assistant'; content: string }>,
    ) => {
      const lifecycle = activeLifecycleRef.current;
      if (!lifecycle) throw new Error('The call is no longer active.');
      const generation = lifecycle.generation;
      const attempt = ++requestAttemptRef.current;
      let acceptingConversationCallbacks = true;
      const isCurrentRequest = () => (
        acceptingConversationCallbacks
        && mountedRef.current
        && activeLifecycleRef.current === lifecycle
        && lifecycleGenerationRef.current === generation
        && requestAttemptRef.current === attempt
      );
      lifecycle.phase = 'creating';
      setPhase('creating');
      setError(null);
      let identifiedConversationId: string | null = null;
      const trackCreatedConversation = (createdId: string) => {
        if (!isNonEmptyString(createdId)) return false;
        const acceptedId = createdId.trim();
        if (!isCurrentRequest()) {
          endConversationOnce(userInfo, acceptedId);
          return false;
        }
        if (lifecycle.conversationId === acceptedId) return true;
        lifecycle.persona = selectedPersona;
        lifecycle.conversationId = acceptedId;
        setConversationId(acceptedId);
        return true;
      };
      const identifyStartedConversation = (startedId: string) => {
        if (!trackCreatedConversation(startedId)) return false;
        const acceptedId = startedId.trim();
        if (identifiedConversationId === acceptedId) return true;
        identifiedConversationId = acceptedId;
        if (presentationRef.current === 'inline') {
          try {
            onConversationIdentifiedRef.current?.(acceptedId);
          } catch (observerError) {
            console.warn('[VideoCallModal] conversation observer failed', observerError);
          }
        }
        return true;
      };
      const seedPreamble = presentationRef.current === 'inline' && openingPreamble
        ? openingPreamble
        : undefined;
      const startRequest = seedPreamble
        ? api.startConversation(
            userInfo,
            selectedPersona,
            chatId,
            [{ role: 'assistant' as const, content: seedPreamble }, ...(conversationalContext ?? [])],
            trackCreatedConversation,
            identifyStartedConversation,
            seedPreamble,
            chapterSlug,
          )
        : api.startConversation(
            userInfo,
            selectedPersona,
            chatId,
            conversationalContext,
            trackCreatedConversation,
            identifyStartedConversation,
            undefined,
            chapterSlug,
          );
      let disposeLateRoom = false;
      void startRequest.then((lateRoom) => {
        if (disposeLateRoom && isNonEmptyString(lateRoom?.conversation_id)) {
          endConversationOnce(userInfo, lateRoom.conversation_id.trim());
        }
      }).catch(() => undefined);
      try {
        const room = await withTimeout(
          startRequest,
          CREATE_CONVERSATION_BUDGET_MS,
          'Starting the conversation took too long. Please retry.',
        );
        if (!isCurrentRequest()) {
          if (isNonEmptyString(room?.conversation_id)) {
            endConversationOnce(userInfo, room.conversation_id.trim());
          }
          throw new Error('The call request is no longer active.');
        }
        if (
          !isNonEmptyString(room?.conversation_id)
          || !isNonEmptyString(room?.conversation_url)
        ) {
          if (isNonEmptyString(room?.conversation_id)) {
            endConversationOnce(userInfo, room.conversation_id.trim());
          }
          throw new Error('The call service returned an incomplete response.');
        }
        const acceptedConversationId = room.conversation_id.trim();
        const acceptedRoomUrl = room.conversation_url.trim();
        if (!identifyStartedConversation(acceptedConversationId)) {
          throw new Error('The call request is no longer active.');
        }
        setRoomUrl(acceptedRoomUrl);
        return acceptedRoomUrl;
      } catch (e: any) {
        const wasCurrentRequest = isCurrentRequest();
        acceptingConversationCallbacks = false;
        disposeLateRoom = true;
        if (wasCurrentRequest) {
          if (lifecycle.conversationId) {
            endConversationOnce(userInfo, lifecycle.conversationId);
            lifecycle.conversationId = null;
            setConversationId(null);
          }
          lifecycle.phase = 'error';
          setError(e?.message || 'We could not start the call. Please try again.');
          setPhase('error');
        }
        throw e;
      }
    },
    [endConversationOnce, userInfo, chapterSlug, openingPreamble],
  );

  const joinCall = useCallback(
    (userName: string, url?: string) => {
      const lifecycle = activeLifecycleRef.current;
      const joinUrl = url || roomUrl;
      if (!callObject || !lifecycle || lifecycle.callObject !== callObject) {
        setError('The call preview is not ready yet. Please retry.');
        if (lifecycle) lifecycle.phase = 'error';
        setPhase('error');
        return;
      }
      if (!joinUrl) {
        setError('No room URL was available. Please restart the call.');
        lifecycle.phase = 'error';
        setPhase('error');
        return;
      }
      lifecycle.phase = 'joining';
      setPhase('joining');
      setError(null);
      callObject.join({ url: joinUrl, userName }).catch((e) => {
        if (activeLifecycleRef.current !== lifecycle) return;
        console.error('Failed to join call', e);
        setError('Joining the call failed. Please try again.');
        lifecycle.phase = 'error';
        setPhase('error');
      });
    },
    [callObject, roomUrl],
  );

  // Watchdog for stuck "joining" state
  useEffect(() => {
    if (phase !== 'joining') return;
    const lifecycle = activeLifecycleRef.current;
    const t = window.setTimeout(() => {
      if (lifecycle && activeLifecycleRef.current === lifecycle && lifecycle.phase === 'joining') {
        setError('Joining the call took too long. Please retry.');
        lifecycle.phase = 'error';
        setPhase('error');
      }
    }, JOIN_CONVERSATION_BUDGET_MS);
    return () => window.clearTimeout(t);
  }, [phase]);

  if (!open || !persona) return null;

  const isInCall = phase === 'joined';
  const isInline = presentation === 'inline';
  const inlineCallScreenProps = isInline
    ? {
        layoutMode: 'learning-workspace' as const,
        openingPreamble,
        onTeachingInteraction,
        onTranscriptVisibilityChange,
      }
    : {};

  const callContent = (
    <>
      {!isInCall && (
        <Tooltip title="Cancel">
          <IconButton
            onClick={onClose}
            aria-label="Cancel video call setup"
            className="video-call-modal-close"
            size="small"
          >
            <CloseRoundedIcon />
          </IconButton>
        </Tooltip>
      )}
      {callObject && (
        <DailyProvider callObject={callObject}>
          {isInCall && conversationId ? (
            <>
              <CallScreen
                conversationId={conversationId}
                leaveCall={onClose}
                persona={persona}
                textChatSessionId={originalChatIdRef.current}
                {...inlineCallScreenProps}
              />
              <DailyAudio />
            </>
          ) : phase === 'error' ? (
            <div className="video-call-modal-error" role="alert">
              <h2>Call setup failed</h2>
              <p>{error || 'Something went wrong.'}</p>
              <button type="button" onClick={onClose}>
                Close
              </button>
            </div>
          ) : (
            <HairCheck
              joinCall={joinCall}
              cancelCall={onClose}
              persona={persona}
              createCall={createCall}
              isCreating={phase === 'creating'}
              onTeachingInteraction={isInline ? onTeachingInteraction : undefined}
              personaSelectionMode={isInline ? 'locked' : 'selectable'}
              continuationChatId={isInline ? continuationChatId : undefined}
            />
          )}
        </DailyProvider>
      )}
    </>
  );

  if (isInline) {
    return <section className="video-call-inline">{callContent}</section>;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isInCall}
      maxWidth="lg"
      fullWidth={!isInCall}
      PaperProps={{
        className: `video-call-modal-paper${isInCall ? ' is-fullscreen' : ''}`,
      }}
      slotProps={{ backdrop: { className: 'video-call-modal-backdrop' } }}
    >
      {callContent}
    </Dialog>
  );
}
